import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, post } from "../services/api";
import type { User } from "../types";
type AuthValue = {
  user: User | null;
  loading: boolean;
  googleEnabled: boolean;
  refresh(): Promise<void>;
  logout(): Promise<void>;
};
const Context = createContext<AuthValue | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null),
    [loading, setLoading] = useState(true),
    [googleEnabled, setGoogle] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const data = await api<{ user: User | null; googleEnabled: boolean }>(
        "/api/auth/session",
      );
      setUser(data.user);
      setGoogle(data.googleEnabled);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const logout = useCallback(async () => {
    await post("/api/auth/logout", {});
    setUser(null);
  }, []);
  const value = useMemo(
    () => ({ user, loading, googleEnabled, refresh, logout }),
    [user, loading, googleEnabled, refresh, logout],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useAuth() {
  const value = useContext(Context);
  if (!value) throw new Error("AuthProvider missing");
  return value;
}
